// Maps standings API response row to a generic table row format
interface StandingsStat {
  id: string
  abbr: string
  value: string | number
}

interface StandingsTeam {
  id: string
  name_full: string
  abbreviation?: string | null
  image?: string | null
}

interface StandingsSchedule {
  id: string
  name: string
}

interface StandingsApiRow {
  team: StandingsTeam
  schedule: StandingsSchedule
  stats: StandingsStat[]
}

interface MappedStandingsRow {
  key: string
  team: StandingsApiRow
  [statAbbr: string]: any
}

/**
 * Maps a standings API response row to a generic table row format
 * Converts the stats array into individual properties keyed by abbreviation
 * @param row - Raw standings row from API
 * @returns Mapped row object suitable for Ant Design table
 */
export const mapStandingsRow = (row: StandingsApiRow): MappedStandingsRow => {
  // Convert stats array to object with abbreviation as key
  const statsRecord = row.stats.reduce<Record<string, any>>((acc, stat) => {
    // Set initial value
    acc[stat.abbr] = stat.value

    // Special handling for IN-DIV stat - format as string when value is 0
    if (stat.abbr === 'IN-DIV' && stat.value === 0) {
      acc[stat.abbr] = '0-0-0-0-0'
    }

    // Format percentage stats with 3 decimal places
    if (['WPCT', 'WPCTD', 'GPCT'].includes(stat.abbr)) {
      acc[stat.abbr] = Number(stat.value).toFixed(3)
    }

    // If the stat is PIM, look for "PiM" in the abbr
    if (stat.abbr === 'PiM') {
      acc.PIM = stat.value
    }

    return acc
  }, {})

  return {
    key: row.team.id,
    team: row,
    ...statsRecord,
  }
}

export default mapStandingsRow
